import { ApiError } from "../api"
import { ensureSession } from "../auth"
import { backendUrl } from "../backend-url"

export type V2ApiInit = Omit<RequestInit, "body" | "headers"> & {
  body?: unknown
  headers?: Record<string, string>
  searchParams?: Record<string, string | number | boolean | undefined | null>
  signal?: AbortSignal
  /** Root catalog loading explicitly changes org context itself. */
  ensureRouteScope?: boolean
}

export type V2ScopeTarget =
  | { kind: "event"; id: string }
  | { kind: "organization"; id: string }
  | null

export function v2ScopeTargetForPath(path: string): V2ScopeTarget {
  const event = path.match(/^\/api\/v2\/events\/([^/?]+)/)
  if (event?.[1]) return { kind: "event", id: decodeURIComponent(event[1]) }
  const organization = path.match(/^\/api\/v2\/organizations\/([^/?]+)/)
  if (organization?.[1]) {
    return { kind: "organization", id: decodeURIComponent(organization[1]) }
  }
  return null
}

async function ensureV2ScopeForPath(
  path: string,
  options: { force?: boolean } = {},
): Promise<void> {
  const target = v2ScopeTargetForPath(path)
  const activeEvent = await import("../active-event")
  if (target?.kind === "event") {
    await activeEvent.ensureActiveOrgForEvent(target.id, options)
  } else if (target?.kind === "organization") {
    await activeEvent.ensureActiveOrganization(target.id, options)
  } else {
    await activeEvent.ensureActiveOrgForCurrentRoute(options)
  }
}

export async function v2Api<T>(path: string, init: V2ApiInit = {}): Promise<T> {
  if (!path.startsWith("/api/v2/")) {
    throw new Error(`The v2 client only accepts canonical /api/v2 paths: ${path}`)
  }

  if (init.ensureRouteScope !== false) {
    await ensureV2ScopeForPath(path)
  }

  const session = await ensureSession()
  if (!session) throw new ApiError("Not signed in", 401, "NOT_SIGNED_IN")

  return sendV2<T>(path, init, false)
}

async function sendV2<T>(
  path: string,
  init: V2ApiInit,
  retried: boolean,
): Promise<T> {
  const headers: Record<string, string> = {
    Accept: "application/json",
    ...(init.headers ?? {}),
  }
  if (init.body !== undefined && !(init.body instanceof FormData)) {
    headers["Content-Type"] = "application/json"
  }

  const res = await fetch(buildV2Url(path, init.searchParams), {
    ...init,
    headers,
    // Organizer actions require Better Auth account-session provenance. The
    // same-origin proxy forwards this cookie without adding another auth method.
    credentials: "include",
    body:
      init.body === undefined
        ? undefined
        : init.body instanceof FormData
          ? init.body
          : JSON.stringify(init.body),
  })

  if (res.status === 401 && !retried) {
    await ensureSession({ force: true })
    return sendV2<T>(path, init, true)
  }
  if (
    res.status === 403 &&
    !retried &&
    init.ensureRouteScope !== false &&
    (await responseErrorCode(res)) === "ACCESS_NO_ORG"
  ) {
    await ensureV2ScopeForPath(path, { force: true })
    return sendV2<T>(path, init, true)
  }

  return parseV2Response<T>(res)
}

async function responseErrorCode(res: Response): Promise<string | undefined> {
  const body = await res.clone().json().catch(() => null) as
    | { code?: string; error?: { code?: string } }
    | null
  return body?.error?.code ?? body?.code
}

function buildV2Url(
  path: string,
  searchParams?: V2ApiInit["searchParams"],
): string {
  if (!searchParams) return backendUrl(path)
  const params = new URLSearchParams()
  for (const [key, value] of Object.entries(searchParams)) {
    if (value === undefined || value === null) continue
    params.append(key, String(value))
  }
  const query = params.toString()
  return backendUrl(query ? `${path}?${query}` : path)
}

async function parseV2Response<T>(res: Response): Promise<T> {
  const contentType = res.headers.get("Content-Type") ?? ""
  const body: unknown = contentType.includes("application/json")
    ? await res.json().catch(() => null)
    : null
  if (!res.ok) {
    const outer = (body ?? {}) as {
      message?: string
      code?: string
      error?: string | { message?: string; code?: string }
    }
    const nested = typeof outer.error === "object" ? outer.error : undefined
    throw new ApiError(
      nested?.message ?? outer.message ??
        (typeof outer.error === "string" ? outer.error : `${res.status} ${res.statusText}`),
      res.status,
      nested?.code ?? outer.code,
      body,
    )
  }
  return body as T
}
